import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { setupAuditInterceptor } from "@britishcouncil/react-common";
import { Layout } from "layout";
import "i18n/config";
import {
  getJourneyStepUrl,
  JOURNEY_STEPS,
  RegistrationJourneyContextProvider,
} from "journey";
import { api } from "api";
import { setupSentryInterceptor } from "utils/setupSentryInterceptor";
import { setupLanguageInterceptor, useSentry } from "hooks";
import { SelectExam } from "pages/SelectExam";
import { TermsAndCondition } from "pages/TermsAndCondition";
import { CandidateDetails } from "pages/CandidateDetails";
import { Summary } from "pages/Summary";
import { Confirmation } from "pages/Confirmation";
import { Payment } from "pages/Payment";
import { PaymentCallback } from "pages/PaymentCallback";
import { GeneralError } from "components";

const queryClient = new QueryClient();
setupLanguageInterceptor(api.instance);
setupAuditInterceptor(api.instance);
setupSentryInterceptor(api.instance);

function App() {
  useSentry();

  const router = createBrowserRouter([
    {
      path: "/",
      Component: Layout,
      children: [
        {
          path: "/",
          element: (
            <RegistrationJourneyContextProvider>
              <Sentry.ErrorBoundary fallback={<GeneralError />}>
                <Outlet />
              </Sentry.ErrorBoundary>
            </RegistrationJourneyContextProvider>
          ),
          children: [
            {
              index: true,
              element: <Navigate to={JOURNEY_STEPS[0].url} replace={true} />,
            },
            {
              path: JOURNEY_STEPS[0].url,
              element: <SelectExam />,
            },
            {
              path: JOURNEY_STEPS[1].url,
              element: <TermsAndCondition />,
            },
            {
              path: JOURNEY_STEPS[2].url,
              element: <CandidateDetails />,
            },
            {
              path: JOURNEY_STEPS[3].url,
              element: <Summary />,
            },
            {
              path: JOURNEY_STEPS[4].url,
              element: <Confirmation />,
            },
          ],
        },
        {
          path: "payment/:guid",
          element: (
            <Sentry.ErrorBoundary fallback={<GeneralError />}>
              <Payment />
            </Sentry.ErrorBoundary>
          ),
        },
        {
          path: "payment/:guid/callback",
          element: (
            <Sentry.ErrorBoundary fallback={<GeneralError />}>
              <PaymentCallback />
            </Sentry.ErrorBoundary>
          ),
        },
        {
          path: "/*",
          element: <Navigate to={getJourneyStepUrl(0)} replace={true} />,
        },
      ],
    },
  ]);

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}

export default App;
